// Registre des compétences et moyens transférés (M24, lot L6-A, H7-07, D-274) : saisi par les comptes
// ministériels autorisés, borné à leur périmètre territorial, versionné (historique sans nom de personne),
// lu par la supervision dans son périmètre. Les données fictives restent distinguées des actes officiels.
import { z } from 'zod';

/** Liste indicative des compétences, à confirmer par l'administration (D-274). */
export const COMPETENCES_TRANSFERABLES = {
  etat_civil: 'État civil',
  urbanisme: 'Urbanisme et aménagement',
  voirie: 'Voirie et éclairage public',
  proprete: 'Propreté et assainissement',
  marches: 'Marchés et équipements marchands',
  enseignement: 'Enseignement préscolaire et primaire',
  sante: 'Santé de base',
  action_sociale: 'Action sociale',
  culture_sport: 'Culture, jeunesse et sports',
  environnement: 'Environnement',
} as const;
export type CodeCompetence = keyof typeof COMPETENCES_TRANSFERABLES;
export const CODES_COMPETENCES = Object.keys(COMPETENCES_TRANSFERABLES) as CodeCompetence[];

export const NATURES_MOYEN = {
  financier: 'Moyens financiers',
  humain: 'Postes transférés',
  materiel: 'Matériel',
  immobilier: 'Biens immobiliers',
} as const;
export type NatureMoyen = keyof typeof NATURES_MOYEN;

export const STATUTS_COMPETENCE = { en_vigueur: 'En vigueur', abrogee: 'Abrogée' } as const;
export type StatutCompetence = keyof typeof STATUTS_COMPETENCE;

const texte = (min: number, max: number) => z.string().trim().min(min).max(max);
const jour = z.iso.date('Date AAAA-MM-JJ attendue');

export const schemaMoyenTransfere = z
  .strictObject({
    nature: z.enum(Object.keys(NATURES_MOYEN) as [NatureMoyen, ...NatureMoyen[]]),
    libelle: texte(2, 200),
    quantite: z.number().nonnegative().max(1e9).nullable().optional(),
    unite: texte(1, 40).nullable().optional(),
    /** Montant annuel en francs CFA (moyens financiers seulement) : colonne financière (D-273). */
    montant_annuel: z.number().int().nonnegative().max(1e13).nullable().optional(),
  })
  .refine((m) => m.nature === 'financier' || m.montant_annuel === null || m.montant_annuel === undefined, {
    message: 'Seuls les moyens financiers portent un montant',
    path: ['montant_annuel'],
  });
export type MoyenTransfere = z.infer<typeof schemaMoyenTransfere>;

const corpsCompetence = {
  competence: z.enum(CODES_COMPETENCES as [CodeCompetence, ...CodeCompetence[]]),
  texte_reference: texte(3, 300),
  date_texte: jour,
  date_effet: jour,
  observation: texte(0, 1000).nullable().optional(),
  moyens: z.array(schemaMoyenTransfere).max(20).default([]),
  motif: texte(3, 500),
};
const datesOrdonnees = (v: { date_texte: string; date_effet: string }) => v.date_effet >= v.date_texte;
const messageDates = { message: 'La date d\'effet suit la date du texte', path: ['date_effet'] };

export const schemaCreationCompetence = z.strictObject({ collectivite_id: z.uuid(), ...corpsCompetence }).refine(datesOrdonnees, messageDates);
export type CreationCompetence = z.infer<typeof schemaCreationCompetence>;

export const schemaModificationCompetence = z
  .strictObject({ ...corpsCompetence, statut: z.enum(Object.keys(STATUTS_COMPETENCE) as [StatutCompetence, ...StatutCompetence[]]).default('en_vigueur') })
  .refine(datesOrdonnees, messageDates);
export type ModificationCompetence = z.infer<typeof schemaModificationCompetence>;

export interface MoyenRestitue {
  nature: NatureMoyen;
  libelle: string;
  quantite: number | null;
  unite: string | null;
  /** Présent seulement avec le droit financier. */
  montant_annuel?: number | null;
}

export interface CompetenceTransferee {
  competence_id: string;
  collectivite_id: string;
  code_court: string;
  nom: string;
  province_code: string;
  competence: CodeCompetence;
  libelle_competence: string;
  texte_reference: string;
  date_texte: string;
  date_effet: string;
  statut: StatutCompetence;
  observation: string | null;
  moyens: MoyenRestitue[];
  fictif: boolean;
  provenance: string;
  version: number;
  modifie_le: string;
  moyens_financiers_montant?: number | null;
}

export interface VersionCompetence {
  version: number;
  date: string;
  motif: string;
  institution: string;
  instantane: Record<string, unknown>;
}
